import { useState, useEffect, useRef } from 'react';
import {
  Trash2, Download, Upload, AlertTriangle, CheckCircle2,
  Database, FileJson, Shield, RefreshCw, ChevronDown, ChevronRight,
  Package, DollarSign, Users, Settings, Factory, RotateCcw,
  Eye, EyeOff, Info, FileArchive, ArrowUpFromLine, ArrowDownToLine,
  Loader2, XCircle, Check, X
} from 'lucide-react';
import { apiGet, apiPost, apiFetch, apiDownload } from '../../lib/api';
import { toast } from 'sonner';

// ── Collection labels (Indonesian) ──────────────────────────────────────────
const COL_LABELS = {
  production_pos: 'Production PO',
  po_items: 'Item PO',
  po_accessories: 'Aksesoris PO',
  vendor_shipments: 'Vendor Shipment',
  vendor_shipment_items: 'Item Vendor Shipment',
  vendor_material_inspections: 'Inspeksi Material Vendor',
  vendor_material_inspection_items: 'Item Inspeksi Material',
  production_jobs: 'Production Job',
  production_job_items: 'Item Production Job',
  production_progress: 'Progress Produksi',
  production_variances: 'Variansi Produksi',
  material_requests: 'Permintaan Material',
  material_defect_reports: 'Laporan Defect Material',
  buyer_shipments: 'Buyer Shipment',
  buyer_shipment_items: 'Item Buyer Shipment',
  production_returns: 'Retur Produksi',
  production_return_items: 'Item Retur Produksi',
  invoices: 'Invoice',
  invoice_adjustments: 'Penyesuaian Invoice',
  invoice_change_history: 'Riwayat Perubahan Invoice',
  invoice_edit_requests: 'Permintaan Edit Invoice',
  payments: 'Pembayaran',
  products: 'Produk',
  product_variants: 'Varian Produk',
  accessories: 'Aksesoris',
  garments: 'Vendor/Garmen',
  buyers: 'Buyer',
  work_orders: 'Work Order',
  activity_logs: 'Log Aktivitas',
  reminders: 'Reminder',
  pdf_export_configs: 'Konfigurasi PDF',
  attachments: 'Lampiran',
  company_settings: 'Pengaturan Perusahaan',
  roles: 'Role',
  permissions: 'Permissions',
  role_permissions: 'Role Permissions',
  accessory_shipments: 'Aksesoris Shipment',
  accessory_shipment_items: 'Item Aksesoris Shipment',
  accessory_inspections: 'Inspeksi Aksesoris',
  accessory_inspection_items: 'Item Inspeksi Aksesoris',
  accessory_defects: 'Defect Aksesoris',
  accessory_requests: 'Permintaan Aksesoris',
  users: 'Akun Pengguna',
};

// ── Delete categories config ─────────────────────────────────────────────────
const DELETE_CATEGORIES = [
  {
    id: 'production',
    label: 'Data Produksi',
    description: 'Hapus semua PO Produksi beserta seluruh turunannya: vendor shipment, inspeksi material, production job, progress, buyer shipment, invoice, pembayaran, retur, variansi, dll.',
    icon: Factory,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10 border-blue-500/20',
    dangerLevel: 'high',
    warning: 'Ini akan menghapus SELURUH rantai data produksi. Tidak dapat dibatalkan.',
  },
  {
    id: 'financial',
    label: 'Data Keuangan',
    description: 'Hapus semua Invoice, Pembayaran, dan Penyesuaian Invoice. PO Produksi dan data shipment tetap ada.',
    icon: DollarSign,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10 border-yellow-500/20',
    dangerLevel: 'medium',
    warning: 'Data invoice dan pembayaran akan hilang permanen.',
  },
  {
    id: 'master',
    label: 'Master Data',
    description: 'Hapus semua data Vendor, Buyer, Produk (beserta varian), dan Aksesoris. Pastikan data produksi sudah dihapus terlebih dahulu.',
    icon: Package,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10 border-purple-500/20',
    dangerLevel: 'high',
    warning: 'Menghapus master data akan membuat data produksi yang ada menjadi tidak referensial.',
  },
  {
    id: 'system',
    label: 'Data Sistem',
    description: 'Hapus Log Aktivitas, Reminder, dan Konfigurasi PDF. Data produksi dan keuangan tidak terpengaruh.',
    icon: Settings,
    color: 'text-teal-400',
    bgColor: 'bg-teal-500/10 border-teal-500/20',
    dangerLevel: 'low',
    warning: 'Riwayat aktivitas dan konfigurasi PDF akan terhapus.',
  },
  {
    id: 'users',
    label: 'Akun Pengguna',
    description: 'Hapus semua akun pengguna kecuali akun Super Admin yang sedang aktif.',
    icon: Users,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10 border-orange-500/20',
    dangerLevel: 'medium',
    warning: 'Semua akun pengguna (admin, vendor, buyer) akan dihapus kecuali Super Admin.',
  },
];

const DANGER_BADGE = {
  high:   { label: 'Bahaya Tinggi', cls: 'bg-red-500/20 text-red-300 border border-red-500/30' },
  medium: { label: 'Bahaya Sedang', cls: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' },
  low:    { label: 'Bahaya Rendah', cls: 'bg-teal-500/20 text-teal-300 border border-teal-500/30' },
};

// ═══════════════════════════════════════════════════════════════════════════════
export default function DataManagementModule({ userRole }) {
  const [activeTab, setActiveTab] = useState('delete');

  if (userRole !== 'superadmin') {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <Shield className="w-16 h-16 text-red-400 opacity-60" />
        <h2 className="text-xl font-semibold text-slate-700">Akses Ditolak</h2>
        <p className="text-slate-500 text-center">
          Fitur ini hanya tersedia untuk <strong>Super Admin</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
          <Database className="w-6 h-6 text-red-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Manajemen Data</h1>
          <p className="text-slate-500 mt-1">
            Hapus data secara selektif (dengan cascade) dan kelola backup / migrasi data.
            Hanya dapat diakses oleh <span className="font-semibold text-red-500">Super Admin</span>.
          </p>
        </div>
      </div>

      {/* Warning banner */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-amber-800">Perhatian</p>
          <p className="text-amber-700 mt-0.5">
            Semua penghapusan data bersifat <strong>permanen dan tidak dapat dibatalkan</strong>.
            Disarankan untuk membuat backup terlebih dahulu sebelum menghapus data.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-0">
        {[
          { id: 'delete', label: 'Hapus Data', icon: Trash2, color: 'text-red-500' },
          { id: 'backup', label: 'Backup & Migrasi', icon: FileArchive, color: 'text-teal-600' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? tab.color : ''}`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'delete' && <DeleteDataTab />}
      {activeTab === 'backup' && <BackupTab />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DELETE DATA TAB
// ═══════════════════════════════════════════════════════════════════════════════
function DeleteDataTab() {
  const [selected, setSelected] = useState(new Set());
  const [isNuclear, setIsNuclear] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  const toggleCategory = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
    setPreview(null);
    setResult(null);
  };

  const toggleNuclear = () => {
    setIsNuclear(v => !v);
    setSelected(new Set());
    setPreview(null);
    setResult(null);
  };

  const getCategories = () => isNuclear ? ['all'] : [...selected];

  const handlePreview = async () => {
    const cats = getCategories();
    if (cats.length === 0) { toast.error('Pilih minimal satu kategori'); return; }
    setPreviewLoading(true);
    setPreview(null);
    try {
      const data = await apiPost('/admin/delete-data/preview', { categories: cats });
      setPreview(data);
    } catch (e) {
      toast.error(e.message || 'Gagal memuat preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExecute = async () => {
    const cats = getCategories();
    const required = isNuclear ? 'HAPUS SEMUA' : 'HAPUS';
    if (confirmInput !== required) {
      toast.error(`Ketik "${required}" untuk konfirmasi`);
      return;
    }
    setExecuting(true);
    try {
      const data = await apiPost('/admin/delete-data/execute', {
        categories: cats,
        confirmation_phrase: confirmInput,
      });
      setResult(data);
      setShowConfirm(false);
      setConfirmInput('');
      setPreview(null);
      setSelected(new Set());
      setIsNuclear(false);
      toast.success(`${data.grand_total} data berhasil dihapus`);
    } catch (e) {
      toast.error(e.message || 'Gagal menghapus data');
    } finally {
      setExecuting(false);
    }
  };

  const requiredPhrase = isNuclear ? 'HAPUS SEMUA' : 'HAPUS';
  const hasSelection = isNuclear || selected.size > 0;

  return (
    <div className="space-y-6">
      {/* Success result */}
      {result && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-800">Penghapusan Berhasil</p>
              <p className="text-sm text-green-600">Total {result.grand_total} record dihapus pada {new Date(result.deleted_at).toLocaleString('id-ID')}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {Object.entries(result.deleted).filter(([, v]) => v > 0).map(([col, cnt]) => (
              <div key={col} className="bg-white border border-green-100 rounded-lg px-3 py-2">
                <p className="text-xs text-slate-500 truncate">{COL_LABELS[col] || col}</p>
                <p className="text-sm font-bold text-green-700">{cnt} dihapus</p>
              </div>
            ))}
          </div>
          <button onClick={() => setResult(null)} className="text-xs text-green-600 underline">Tutup</button>
        </div>
      )}

      {/* Nuclear option */}
      <div
        onClick={toggleNuclear}
        data-testid="nuclear-option"
        className={`cursor-pointer rounded-xl border-2 p-5 transition-all ${
          isNuclear
            ? 'border-red-500 bg-red-50 shadow-md shadow-red-100'
            : 'border-slate-200 bg-white hover:border-red-300 hover:bg-red-50/30'
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`p-2 rounded-lg ${isNuclear ? 'bg-red-100' : 'bg-slate-100'}`}>
              <Trash2 className={`w-5 h-5 ${isNuclear ? 'text-red-600' : 'text-slate-500'}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-bold text-slate-800">Hapus Semua Data</p>
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">NUCLEAR</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Hapus semua data kecuali pengaturan perusahaan, role/permission sistem, dan akun Super Admin.
                Perlu konfirmasi <code className="bg-red-100 text-red-700 px-1 rounded font-mono text-xs">HAPUS SEMUA</code>.
              </p>
            </div>
          </div>
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${
            isNuclear ? 'bg-red-500 border-red-500' : 'border-slate-300'
          }`}>
            {isNuclear && <Check className="w-3 h-3 text-white" />}
          </div>
        </div>
      </div>

      {/* Divider */}
      {!isNuclear && (
        <div className="flex items-center gap-3">
          <div className="flex-1 border-t border-slate-200" />
          <span className="text-xs text-slate-400 font-medium">ATAU PILIH KATEGORI</span>
          <div className="flex-1 border-t border-slate-200" />
        </div>
      )}

      {/* Category cards */}
      {!isNuclear && (
        <div className="grid gap-4">
          {DELETE_CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const isSelected = selected.has(cat.id);
            const badge = DANGER_BADGE[cat.dangerLevel];
            const catPreview = preview?.preview?.[cat.id];
            const isExpanded = expandedCategory === cat.id;

            return (
              <div
                key={cat.id}
                className={`rounded-xl border-2 transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50/50 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div
                  className="flex items-start gap-4 p-4 cursor-pointer"
                  onClick={() => toggleCategory(cat.id)}
                  data-testid={`category-${cat.id}`}
                >
                  <div className={`p-2 rounded-lg border ${cat.bgColor} flex-shrink-0 mt-0.5`}>
                    <Icon className={`w-5 h-5 ${cat.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-800">{cat.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                      {catPreview && (
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono">
                          {catPreview.total} record
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{cat.description}</p>
                    {isSelected && (
                      <div className="flex items-center gap-1.5 mt-2 text-amber-600 text-xs">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {cat.warning}
                      </div>
                    )}
                  </div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${
                    isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-300'
                  }`}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>

                {/* Preview breakdown */}
                {catPreview && isSelected && (
                  <div className="border-t border-slate-100 mx-4 mb-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpandedCategory(isExpanded ? null : cat.id); }}
                      className="flex items-center gap-1.5 text-xs text-blue-600 font-medium mt-2 hover:underline"
                    >
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      Lihat detail ({catPreview.total} record di {Object.keys(catPreview.collections).length} koleksi)
                    </button>
                    {isExpanded && (
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1.5">
                        {Object.entries(catPreview.collections).filter(([, v]) => v > 0).map(([col, cnt]) => (
                          <div key={col} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded px-2 py-1">
                            <span className="text-xs text-slate-500 truncate">{COL_LABELS[col] || col}</span>
                            <span className="text-xs font-bold text-red-600 ml-2 flex-shrink-0">{cnt}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Actions */}
      {hasSelection && (
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button
            onClick={handlePreview}
            disabled={previewLoading}
            data-testid="preview-delete-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Preview Data yang Akan Dihapus
          </button>
          {preview && (
            <button
              onClick={() => setShowConfirm(true)}
              data-testid="open-confirm-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Hapus {preview.grand_total} Record
            </button>
          )}
        </div>
      )}

      {/* Preview summary */}
      {preview && !showConfirm && (
        <div className="bg-white border-2 border-orange-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0" />
            <div>
              <p className="font-bold text-slate-800">Preview Penghapusan</p>
              <p className="text-sm text-slate-500">Total {preview.grand_total} record akan dihapus secara permanen</p>
            </div>
          </div>
          <div className="grid gap-3">
            {Object.entries(preview.preview).map(([catId, catData]) => (
              <div key={catId} className="bg-orange-50 border border-orange-100 rounded-lg p-3">
                <p className="text-sm font-semibold text-orange-800">{catData.label}</p>
                <p className="text-xs text-orange-600 mt-0.5">{catData.total} record</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-xl flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Konfirmasi Penghapusan</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Tindakan ini akan menghapus <strong className="text-red-600">{preview?.grand_total} record</strong> secara permanen.
                  Tidak dapat dibatalkan.
                </p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
              {Object.entries(preview?.preview || {}).map(([catId, catData]) => (
                <div key={catId} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{catData.label}</span>
                  <span className="font-bold text-red-600">{catData.total}</span>
                </div>
              ))}
              <div className="border-t border-red-200 pt-2 flex items-center justify-between font-bold">
                <span className="text-slate-800">TOTAL</span>
                <span className="text-red-700">{preview?.grand_total}</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Ketik <code className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-mono">{requiredPhrase}</code> untuk konfirmasi:
              </label>
              <input
                type="text"
                value={confirmInput}
                onChange={e => setConfirmInput(e.target.value)}
                placeholder={requiredPhrase}
                data-testid="confirm-input"
                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-red-400 font-mono"
                autoComplete="off"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setShowConfirm(false); setConfirmInput(''); }}
                className="flex-1 px-4 py-2.5 rounded-xl border-2 border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
                data-testid="cancel-confirm-btn"
              >
                Batal
              </button>
              <button
                onClick={handleExecute}
                disabled={executing || confirmInput !== requiredPhrase}
                data-testid="execute-delete-btn"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {executing ? 'Menghapus...' : 'Hapus Sekarang'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BACKUP & MIGRATION TAB
// ═══════════════════════════════════════════════════════════════════════════════
function BackupTab() {
  const [activeSection, setActiveSection] = useState('export');
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [exportAll, setExportAll] = useState(true);
  const [selectedExport, setSelectedExport] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  // Import state
  const fileInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importPreviewLoading, setImportPreviewLoading] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    if (activeSection === 'export') fetchCollections();
  }, [activeSection]);

  const fetchCollections = async () => {
    setCollectionsLoading(true);
    try {
      const data = await apiGet('/admin/backup/collections');
      setCollections(data.collections || []);
    } catch (e) {
      toast.error('Gagal memuat daftar koleksi');
    } finally {
      setCollectionsLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const colList = exportAll ? null : [...selectedExport];
      // Use apiFetch for the streaming response
      const res = await apiFetch('/admin/backup/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ collections: colList }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Gagal export backup');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      a.href = url;
      a.download = `garment_erp_backup_${timestamp}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Backup berhasil diunduh');
    } catch (e) {
      toast.error(e.message || 'Gagal export backup');
    } finally {
      setExporting(false);
    }
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImportFile(f);
    setImportPreview(null);
    setImportResult(null);

    // Auto-preview
    setImportPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await apiFetch('/admin/backup/import/preview', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Gagal preview file');
      }
      const data = await res.json();
      setImportPreview(data);
    } catch (e) {
      toast.error(e.message || 'Gagal membaca file backup');
      setImportFile(null);
    } finally {
      setImportPreviewLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('mode', importMode);
      const res = await apiFetch('/admin/backup/import', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Gagal import backup');
      }
      const data = await res.json();
      setImportResult(data);
      setImportFile(null);
      setImportPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success(`Import berhasil: ${data.total_documents} dokumen diproses`);
    } catch (e) {
      toast.error(e.message || 'Gagal import backup');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Section switcher */}
      <div className="flex gap-3">
        {[
          { id: 'export', label: 'Export Backup', icon: ArrowDownToLine, desc: 'Unduh data sebagai file ZIP' },
          { id: 'import', label: 'Import / Restore', icon: ArrowUpFromLine, desc: 'Pulihkan data dari file backup' },
        ].map(sec => (
          <button
            key={sec.id}
            onClick={() => setActiveSection(sec.id)}
            data-testid={`backup-section-${sec.id}`}
            className={`flex-1 flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
              activeSection === sec.id
                ? 'border-teal-500 bg-teal-50'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className={`p-2 rounded-lg ${activeSection === sec.id ? 'bg-teal-100' : 'bg-slate-100'}`}>
              <sec.icon className={`w-5 h-5 ${activeSection === sec.id ? 'text-teal-600' : 'text-slate-500'}`} />
            </div>
            <div>
              <p className={`font-semibold text-sm ${activeSection === sec.id ? 'text-teal-800' : 'text-slate-700'}`}>{sec.label}</p>
              <p className="text-xs text-slate-400 mt-0.5">{sec.desc}</p>
            </div>
          </button>
        ))}
      </div>

      {/* ── EXPORT ── */}
      {activeSection === 'export' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <div>
            <h3 className="font-semibold text-slate-800 text-lg">Export Backup</h3>
            <p className="text-sm text-slate-500 mt-1">
              Unduh semua data ERP sebagai file ZIP. File ini dapat digunakan untuk migrasi ke server baru atau sebagai cadangan sebelum update.
            </p>
          </div>

          {/* Export scope selector */}
          <div className="space-y-3">
            <div className="flex gap-3">
              <button
                onClick={() => setExportAll(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                  exportAll ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
                data-testid="export-all-btn"
              >
                <Database className="w-4 h-4" />
                Semua Koleksi
              </button>
              <button
                onClick={() => setExportAll(false)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                  !exportAll ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
                data-testid="export-select-btn"
              >
                <FileJson className="w-4 h-4" />
                Pilih Koleksi
              </button>
            </div>

            {/* Collection list for selective export */}
            {!exportAll && (
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between border-b border-slate-200">
                  <span className="text-sm font-medium text-slate-700">Pilih koleksi untuk diexport</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedExport(new Set(collections.map(c => c.name)))}
                      className="text-xs text-blue-600 hover:underline"
                    >Pilih Semua</button>
                    <span className="text-slate-300">|</span>
                    <button
                      onClick={() => setSelectedExport(new Set())}
                      className="text-xs text-slate-500 hover:underline"
                    >Kosongkan</button>
                  </div>
                </div>
                {collectionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                    {collections.map(col => (
                      <label
                        key={col.name}
                        className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedExport.has(col.name)}
                          onChange={() => {
                            const next = new Set(selectedExport);
                            if (next.has(col.name)) next.delete(col.name); else next.add(col.name);
                            setSelectedExport(next);
                          }}
                          className="w-4 h-4 rounded border-slate-300 accent-teal-600"
                        />
                        <span className="flex-1 text-sm text-slate-700">{COL_LABELS[col.name] || col.name}</span>
                        <span className="text-xs text-slate-400 font-mono">{col.count} doc</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Summary */}
            {!exportAll && !collectionsLoading && (
              <p className="text-sm text-slate-500">
                {selectedExport.size} koleksi dipilih ·{' '}
                {collections.filter(c => selectedExport.has(c.name)).reduce((s, c) => s + c.count, 0)} dokumen
              </p>
            )}
            {exportAll && !collectionsLoading && collections.length > 0 && (
              <p className="text-sm text-slate-500">
                {collections.length} koleksi ·{' '}
                {collections.reduce((s, c) => s + c.count, 0)} dokumen total
              </p>
            )}
          </div>

          {/* Info cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { icon: FileArchive, title: 'Format ZIP', desc: 'Berisi metadata.json + JSON per koleksi' },
              { icon: Shield, title: 'Aman', desc: 'Password hash tetap terenkripsi' },
              { icon: RefreshCw, title: 'Restorable', desc: 'Dapat direstore ke instalasi baru' },
            ].map((info, i) => (
              <div key={i} className="flex items-start gap-3 bg-teal-50 border border-teal-100 rounded-xl p-3">
                <info.icon className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-teal-800">{info.title}</p>
                  <p className="text-xs text-teal-600 mt-0.5">{info.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={handleExport}
            disabled={exporting || (!exportAll && selectedExport.size === 0)}
            data-testid="export-download-btn"
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-teal-600 text-white font-semibold text-sm hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {exporting ? 'Menyiapkan backup...' : 'Unduh Backup'}
          </button>
        </div>
      )}

      {/* ── IMPORT ── */}
      {activeSection === 'import' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <div>
            <h3 className="font-semibold text-slate-800 text-lg">Import / Restore Data</h3>
            <p className="text-sm text-slate-500 mt-1">
              Pulihkan data dari file backup ZIP. Pilih mode <strong>Merge</strong> untuk menggabungkan data atau <strong>Replace</strong> untuk mengganti.
            </p>
          </div>

          {/* Mode selector */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Mode Import</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                {
                  id: 'merge',
                  label: 'Merge (Gabung)',
                  icon: RefreshCw,
                  desc: 'Data baru ditambahkan, data yang sudah ada diperbarui berdasarkan ID. Aman untuk data yang ada.',
                  safe: true,
                },
                {
                  id: 'replace',
                  label: 'Replace (Ganti)',
                  icon: RotateCcw,
                  desc: 'Koleksi tujuan dikosongkan terlebih dahulu, lalu data dari backup dimasukkan. Data yang ada akan TERHAPUS.',
                  safe: false,
                },
              ].map(m => (
                <div
                  key={m.id}
                  onClick={() => setImportMode(m.id)}
                  data-testid={`import-mode-${m.id}`}
                  className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                    importMode === m.id
                      ? m.safe ? 'border-teal-500 bg-teal-50' : 'border-orange-500 bg-orange-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <m.icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${importMode === m.id ? (m.safe ? 'text-teal-600' : 'text-orange-600') : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-semibold text-sm ${importMode === m.id ? (m.safe ? 'text-teal-800' : 'text-orange-800') : 'text-slate-700'}`}>
                        {m.label}
                      </p>
                      <p className={`text-xs mt-1 ${importMode === m.id ? (m.safe ? 'text-teal-600' : 'text-orange-600') : 'text-slate-400'}`}>
                        {m.desc}
                      </p>
                    </div>
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                      importMode === m.id ? (m.safe ? 'border-teal-500' : 'border-orange-500') : 'border-slate-300'
                    }`}>
                      {importMode === m.id && <div className={`w-2 h-2 rounded-full ${m.safe ? 'bg-teal-500' : 'bg-orange-500'}`} />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* File upload */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Upload File Backup (.zip)</p>
            <label
              htmlFor="backup-file"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors"
              data-testid="backup-file-drop-zone"
            >
              <Upload className="w-8 h-8 text-slate-400 mb-2" />
              <p className="text-sm text-slate-500">
                {importFile ? (
                  <span className="text-teal-600 font-medium">{importFile.name}</span>
                ) : (
                  <>Klik untuk pilih file <span className="text-teal-600 font-medium">.zip</span></>
                )}
              </p>
              <p className="text-xs text-slate-400 mt-1">Format: garment_erp_backup_*.zip</p>
            </label>
            <input
              ref={fileInputRef}
              id="backup-file"
              type="file"
              accept=".zip"
              className="hidden"
              onChange={handleFileChange}
              data-testid="backup-file-input"
            />
          </div>

          {/* Preview loading */}
          {importPreviewLoading && (
            <div className="flex items-center gap-3 text-sm text-slate-500 bg-slate-50 rounded-xl p-4">
              <Loader2 className="w-5 h-5 animate-spin text-teal-500" />
              Membaca file backup...
            </div>
          )}

          {/* Import preview */}
          {importPreview && !importPreviewLoading && (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-teal-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-teal-800">File Backup Valid</p>
                  <p className="text-sm text-teal-600">
                    Dibuat: {importPreview.metadata?.exported_at
                      ? new Date(importPreview.metadata.exported_at).toLocaleString('id-ID')
                      : '—'} ·
                    Versi: {importPreview.metadata?.app_version || '—'} ·
                    Oleh: {importPreview.metadata?.exported_by || '—'}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-teal-800 mb-2">
                  {importPreview.collections?.length} koleksi · {importPreview.total_documents} dokumen
                </p>
                <div className="max-h-48 overflow-y-auto border border-teal-200 rounded-lg divide-y divide-teal-100 bg-white">
                  {importPreview.collections?.map(col => (
                    <div key={col.name} className="flex items-center justify-between px-3 py-2">
                      <div className="flex items-center gap-2">
                        {col.available
                          ? <Check className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                          : <X className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />}
                        <span className="text-sm text-slate-700">{COL_LABELS[col.name] || col.name}</span>
                      </div>
                      <span className="text-xs text-slate-500 font-mono">{col.count} doc</span>
                    </div>
                  ))}
                </div>
              </div>

              {importMode === 'replace' && (
                <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-xl p-3">
                  <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-orange-700">
                    <strong>Mode Replace:</strong> Semua koleksi yang ada di file backup akan dikosongkan terlebih dahulu sebelum data diimport. Pastikan sudah membuat backup terlebih dahulu.
                  </p>
                </div>
              )}

              <button
                onClick={handleImport}
                disabled={importing}
                data-testid="import-execute-btn"
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-teal-600 text-white font-semibold text-sm hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {importing ? 'Mengimport...' : `Import (${importMode === 'merge' ? 'Merge' : 'Replace'})`}
              </button>
            </div>
          )}

          {/* Import result */}
          {importResult && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-green-800">Import Berhasil!</p>
                  <p className="text-sm text-green-600">
                    {importResult.total_documents} dokumen diproses pada {new Date(importResult.imported_at).toLocaleString('id-ID')}
                  </p>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto border border-green-200 rounded-lg divide-y divide-green-100 bg-white">
                {Object.entries(importResult.results || {}).map(([col, res]) => (
                  <div key={col} className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm text-slate-700">{COL_LABELS[col] || col}</span>
                    <div className="flex items-center gap-3 text-xs">
                      {res.inserted > 0 && <span className="text-green-600 font-medium">+{res.inserted} baru</span>}
                      {res.updated > 0 && <span className="text-blue-600 font-medium">~{res.updated} update</span>}
                      {res.errors > 0 && <span className="text-red-500 font-medium">{res.errors} error</span>}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => setImportResult(null)} className="text-xs text-green-600 underline">Tutup</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
